// Canonical seed list of threat categories — the use-case-style grouping that
// threats are bucketed by across the heatmap, framework coverage, and
// reports. This is DISTINCT from the external framework taxonomies
// (modules/mappings/frameworks/) that threats are separately mapped to; a
// category groups threats, a framework maps them.
//
// Editable + extensible from /admin (Threats tab). Runtime precedence is
// seed -> content/baseline -> content/overrides (see modules/content/resolve.ts,
// resolveThreatCategories()). `order` controls display order; `operational: true`
// exempts a category's threats from the "must map to a framework" validator
// rule — all three categories here are marked operational because this app
// ships without an external cross-reference catalog (OWASP/MITRE-style) for
// the infrastructure-defense domain yet.

import type { ThreatCategoryDef } from "../shared/types";

export const THREAT_CATEGORIES: ThreatCategoryDef[] = [
  { id: "INFRASTRUCTURE", label: "Infrastructure", order: 1, operational: true },
  { id: "IDENTITY_AGENT", label: "Identity & Agent", order: 2, operational: true },
  { id: "OPERATIONAL", label: "Operational", order: 3, operational: true },
];

// Ordered label map derived from the seed list. Kept for back-compat with the
// former static `THREAT_CATEGORY_LABELS` constant; prefer resolving the list via
// the content module (useThreatCategories) so admin edits are honored.
export function categoryLabelsFrom(
  categories: ThreatCategoryDef[] = THREAT_CATEGORIES,
): Record<string, string> {
  return Object.fromEntries(
    [...categories]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((c) => [c.id, c.label]),
  );
}

// Is a threat's category flagged operational (framework mapping not required)?
export function isOperationalCategory(
  categoryId: string,
  categories: ThreatCategoryDef[] = THREAT_CATEGORIES,
): boolean {
  return categories.find((c) => c.id === categoryId)?.operational === true;
}
